import type { ComputedDrawingSet } from "bear/drawings/types";
import type { QueryStep } from "bear/engine/query-plan";
import type { TestReport } from "bear/test";
import { debug } from "debug";
import { invokeAgentTools } from "playground/agent-tools/dispatch";
import type { Playground } from "playground/hal/index";
import {
  createSandboxHost,
  type HostPost,
  type SandboxHost,
} from "playground/sandbox";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invariant } from "ts-invariant";

import type { ModelExport } from "@/bear-sandbox/model-export";
import type { SerializedNode } from "@/bear-sandbox/query";
import type { ModelDiagnostic } from "@/bear-sandbox/types";
import type {
  BearChildToParent,
  ParentToChild,
  SerializableViewDefinition,
} from "@/bear-sandbox/types";
import {
  agentToolsFor,
  notifyCameraView,
  notifyContextMenu,
  notifyPlaneOpen,
  notifyPlanePick,
  notifySelectionChange,
  recordSandboxConsoleLog,
  rejectExecution,
  resolveExecution,
  setSandboxClearFn,
  setSandboxCommandFn,
  setSandboxConnectionState,
  setSandboxChecksFn,
  setSandboxDrawingSetFn,
  setSandboxEvaluator,
  setSandboxQueryFn,
  setSandboxCaptureViewFn,
  setSandboxSectionViewFn,
  type ConsumerCommand,
  type SandboxEvalResult,
} from "@/lib/bear-sandbox-bridge";
import { newSessionId } from "@/lib/id";

const log = debug("bear:BearSandbox");

export type BuildPayload = ModelExport;

export type BearSandboxProps = {
  /** Build configuration — just the entry point; files are loaded lazily */
  buildPayload?: BuildPayload;
  /** Parent-side Playground the sandbox proxies to over IPC. */
  playground: Playground;
  onExecutionError?: (error: Error) => void;
  onWorkspacePresent?: (paths: string[]) => Promise<void>;
};

type PendingRpc = {
  resolve: (v: unknown) => void;
  reject: (e: unknown) => void;
  timer: number;
};

/**
 * Map bear's request types onto the child→parent response types so the host
 * can resolve / reject the right pending promise. The `eval` timeout is
 * generous because IFC imports take minutes; everything else is bounded
 * geometry/query work that should fail fast.
 */
const REQUEST_CONFIG: Record<
  string,
  { result: string; error?: string; timeoutMs?: number }
> = {
  ping: { result: "pong" },
  build: { result: "materialize_result", error: "materialize_error" },
  materialize: { result: "materialize_result", error: "materialize_error" },
  eval: {
    result: "eval_result",
    error: "eval_error",
    timeoutMs: 10 * 60_000,
  },
  eval_clear: { result: "eval_result" },
  query: { result: "query_result", error: "query_error" },
  capture_view: { result: "capture_view_result", error: "capture_view_error" },
  compute_section_view: {
    result: "section_view_result",
    error: "section_view_error",
  },
  compute_drawing_set: {
    result: "drawing_set_result",
    error: "drawing_set_error",
  },
  // Clash and clearance checks intersect solids across a whole building.
  run_checks: {
    result: "checks_result",
    error: "checks_error",
    timeoutMs: 10 * 60_000,
  },
};

const DEFAULT_TIMEOUT_MS = 120_000;

class SandboxDisposedError extends Error {
  constructor() {
    super("sandbox host disposed");
  }
}

type BearRpcInput =
  | { kind: "parent"; type: "ping" }
  | {
      kind: "parent";
      type: "build";
      entryPoint?: string;
      exportName?: string;
      props?: Record<string, unknown>;
    }
  | {
      kind: "parent";
      type: "eval";
      code: string;
      execution?: string;
    }
  | { kind: "parent"; type: "eval_clear" }
  | { kind: "parent"; type: "capture_view" }
  | { kind: "parent"; type: "query"; query: QueryStep[] }
  | {
      kind: "parent";
      type: "compute_section_view";
      view: SerializableViewDefinition;
    }
  | { kind: "parent"; type: "compute_drawing_set"; entryPoint: string }
  | { kind: "parent"; type: "run_checks"; files: string[] };

const BearSandbox = ({
  buildPayload,
  playground,
  onExecutionError,
  onWorkspacePresent,
}: BearSandboxProps) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const hostRef = useRef<SandboxHost | null>(null);
  const [connected, setConnected] = useState(false);

  // Prefix for RPC ids, unique per mount so a pending entry can never collide
  // with one from a previous iframe generation.
  const idPrefix = useMemo(() => newSessionId(), []);

  const pending = useRef(new Map<string, PendingRpc>());
  const presenterRef = useRef(onWorkspacePresent);
  presenterRef.current = onWorkspacePresent;
  const idCounter = useRef(0);

  // The host lives as long as this mount, not the iframe's ref: a Suspense
  // boundary hiding the view detaches refs while the iframe stays loaded.
  useEffect(() => {
    const node = iframeRef.current;
    invariant(node, "sandbox iframe is not mounted");
    const host = createSandboxHost({
      iframe: node,
      playground,
    });
    hostRef.current = host;

    // Track connection state for the React tree
    host.onConnectionChange(setConnected);

    // Wire bear's request → response RPC map. Every bear response type
    // resolves the pending promise keyed by id; error types reject it.
    const unsubs: (() => void)[] = [];
    const resolveById = (id: string, value: unknown) => {
      const p = pending.current.get(id);
      if (!p) return;
      clearTimeout(p.timer);
      p.resolve(value);
      pending.current.delete(id);
    };
    const rejectById = (id: string, err: Error) => {
      const p = pending.current.get(id);
      if (!p) return;
      clearTimeout(p.timer);
      p.reject(err);
      pending.current.delete(id);
    };

    // pong → resolves with the literal "pong"
    unsubs.push(
      host.on<{ id: string }>("pong", (m) => resolveById(m.id, "pong")),
    );
    unsubs.push(
      host.on<{ id: string; diagnostics: readonly ModelDiagnostic[] }>(
        "materialize_result",
        (m) => {
          resolveById(m.id, m.diagnostics);
          log("model diagnostics", m.diagnostics);
          resolveExecution(m.diagnostics);
        },
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("materialize_error", (m) => {
        const err = new Error(m.error);
        rejectById(m.id, err);
        rejectExecution(err);
        onExecutionError?.(err);
      }),
    );
    unsubs.push(
      host.on<{
        id: string;
        result: string;
        isExpression?: boolean;
        mediaType?: string;
      }>("eval_result", (m) => {
        resolveById(m.id, {
          result: m.result,
          isExpression: m.isExpression,
          mediaType: m.mediaType,
        });
      }),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("eval_error", (m) => {
        rejectById(m.id, new Error(m.error));
      }),
    );
    unsubs.push(
      host.on<{ id: string; result: unknown }>("query_result", (m) =>
        resolveById(m.id, m.result),
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("query_error", (m) =>
        rejectById(m.id, new Error(m.error)),
      ),
    );
    unsubs.push(
      host.on<{ id: string; result: unknown }>("capture_view_result", (m) =>
        resolveById(m.id, m.result),
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("capture_view_error", (m) =>
        rejectById(m.id, new Error(m.error)),
      ),
    );
    unsubs.push(
      host.on<{ id: string; result: unknown }>("section_view_result", (m) =>
        resolveById(m.id, m.result),
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("section_view_error", (m) =>
        rejectById(m.id, new Error(m.error)),
      ),
    );
    unsubs.push(
      host.on<{ id: string; result: unknown }>("drawing_set_result", (m) =>
        resolveById(m.id, m.result),
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("drawing_set_error", (m) =>
        rejectById(m.id, new Error(m.error)),
      ),
    );
    unsubs.push(
      host.on<{ id: string; result: unknown }>("checks_result", (m) =>
        resolveById(m.id, m.result),
      ),
    );
    unsubs.push(
      host.on<{ id: string; error: string }>("checks_error", (m) =>
        rejectById(m.id, new Error(m.error)),
      ),
    );
    // An agent eval's tool calls, answered by that eval's own tools.
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "agent_tools" }>>(
        "agent_tools",
        (m) => {
          void Promise.resolve()
            .then(() =>
              invokeAgentTools(agentToolsFor(m.execution), m.method, m.args),
            )
            .then(
              (result) =>
                host.post({
                  kind: "parent",
                  type: "agent_tools_result",
                  id: m.id,
                  result,
                }),
              (error: unknown) =>
                host.post({
                  kind: "parent",
                  type: "agent_tools_result",
                  id: m.id,
                  error: error instanceof Error ? error.message : String(error),
                }),
            );
        },
      ),
    );
    // Selection + console + keyboard are fire-and-forget side channels.
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "workspace_present" }>>(
        "workspace_present",
        (m) => {
          void Promise.resolve()
            .then(() => {
              if (!presenterRef.current) {
                throw new Error("Task workspace presentation is unavailable");
              }
              return presenterRef.current(m.paths);
            })
            .then(
              () =>
                host.post({
                  kind: "parent",
                  type: "workspace_present_result",
                  id: m.id,
                }),
              (error: unknown) =>
                host.post({
                  kind: "parent",
                  type: "workspace_present_result",
                  id: m.id,
                  error: error instanceof Error ? error.message : String(error),
                }),
            );
        },
      ),
    );
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "selection_change" }>>(
        "selection_change",
        (m) => notifySelectionChange(m.nodeIds),
      ),
    );
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "camera_change" }>>(
        "camera_change",
        (m) => notifyCameraView(m.view),
      ),
    );
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "plane_pick" }>>(
        "plane_pick",
        (m) => notifyPlanePick(m.pick),
      ),
    );
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "open_plane" }>>(
        "open_plane",
        (m) => notifyPlaneOpen(m.planeId),
      ),
    );
    unsubs.push(
      host.on<Extract<BearChildToParent, { type: "context_menu" }>>(
        "context_menu",
        (m) => notifyContextMenu({ x: m.x, y: m.y }),
      ),
    );
    unsubs.push(
      host.on<{
        id: string;
        level: import("@/console").SandboxConsoleLevel;
        args: string[];
        timestamp: number;
      }>("console", (m) =>
        recordSandboxConsoleLog({
          id: m.id,
          level: m.level,
          args: m.args,
          timestamp: m.timestamp,
        }),
      ),
    );
    unsubs.push(
      host.on<{ key: string; metaKey: boolean; ctrlKey: boolean }>(
        "keyboard",
        (m) => {
          window.dispatchEvent(
            new KeyboardEvent("keydown", {
              key: m.key,
              metaKey: m.metaKey,
              ctrlKey: m.ctrlKey,
              bubbles: true,
            }),
          );
        },
      ),
    );

    const calls = pending.current;
    return () => {
      // Disposal closes the private channel, so in-flight replies can never
      // arrive: fail the pending RPCs now rather than at their timeouts.
      for (const u of unsubs) u();
      host.dispose();
      hostRef.current = null;
      setConnected(false);
      for (const [, p] of calls) {
        clearTimeout(p.timer);
        p.reject(new SandboxDisposedError());
      }
      calls.clear();
    };
  }, [playground, onExecutionError]);

  const post: HostPost = useCallback((msg) => {
    const host = hostRef.current;
    if (!host) throw new Error("iframe not ready");
    host.post(msg);
  }, []);

  const rpc = useCallback(
    (msg: BearRpcInput): Promise<unknown> => {
      const id = `${idPrefix}:${idCounter.current++}`;
      const config = REQUEST_CONFIG[msg.type];
      const timeoutMs = config?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
      return new Promise((resolve, reject) => {
        const timer = window.setTimeout(() => {
          pending.current.delete(id);
          reject(new Error(`${msg.type} timed out after ${timeoutMs}ms`));
        }, timeoutMs);
        pending.current.set(id, { resolve, reject, timer });
        post({ ...msg, id } as ParentToChild);
      });
    },
    [post, idPrefix],
  );

  useEffect(() => {
    if (!connected || !buildPayload) return;
    // A build reports through `materialize_*`; only its teardown is expected.
    rpc({ kind: "parent", type: "build", ...buildPayload }).catch(
      (error: unknown) => {
        if (!(error instanceof SandboxDisposedError)) throw error;
      },
    );
  }, [connected, buildPayload, rpc]);

  useEffect(() => {
    setSandboxConnectionState(connected);
    if (!connected) {
      setSandboxEvaluator(null);
      setSandboxClearFn(null);
      setSandboxQueryFn(null);
      setSandboxCommandFn(null);
      setSandboxSectionViewFn(null);
      return;
    }

    setSandboxEvaluator(
      async (opts: {
        code: string;
        execution?: string;
      }): Promise<SandboxEvalResult> => {
        try {
          const { result, isExpression, mediaType } = (await rpc({
            kind: "parent",
            type: "eval",
            code: opts.code,
            execution: opts.execution,
          })) as { result: string; isExpression?: boolean; mediaType?: string };
          return { type: "result", value: result, mediaType, isExpression };
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : String(error ?? "Unknown error");
          return { type: "error", value: message };
        }
      },
    );

    setSandboxClearFn(async () => {
      // Runtime acks eval_clear with an empty eval_result; the eval_result
      // listener resolves the pending promise.
      await rpc({ kind: "parent", type: "eval_clear" });
    });

    setSandboxQueryFn(
      async (query) =>
        // The runtime answers with `handleQuery`'s serialized nodes.
        (await rpc({
          kind: "parent",
          type: "query",
          query: [...query],
        })) as SerializedNode[],
    );

    setSandboxCommandFn((cmd: ConsumerCommand) => {
      post({
        kind: "parent",
        ...cmd,
        id: `cmd:${idCounter.current++}`,
      } as ParentToChild);
    });

    setSandboxCaptureViewFn(async () => {
      const result = await rpc({ kind: "parent", type: "capture_view" });
      return result as string;
    });

    setSandboxSectionViewFn(async (view) => {
      const result = await rpc({
        kind: "parent",
        type: "compute_section_view",
        view,
      });
      return result as import("bear/drawings/types").SectionViewResult;
    });

    setSandboxDrawingSetFn(async (entryPoint) => {
      const result = await rpc({
        kind: "parent",
        type: "compute_drawing_set",
        entryPoint,
      });
      return result as ComputedDrawingSet;
    });

    setSandboxChecksFn(
      async (files) =>
        (await rpc({
          kind: "parent",
          type: "run_checks",
          files,
        })) as TestReport,
    );

    return () => {
      setSandboxEvaluator(null);
      setSandboxClearFn(null);
      setSandboxQueryFn(null);
      setSandboxCommandFn(null);
      setSandboxCaptureViewFn(null);
      setSandboxSectionViewFn(null);
      setSandboxDrawingSetFn(null);
      setSandboxChecksFn(null);
      setSandboxConnectionState(false);
    };
  }, [connected, rpc, post]);

  return (
    <iframe
      ref={iframeRef}
      title="sandbox"
      // `display: block` is required: a default inline iframe reserves a ~4px
      // baseline descender strip below itself, making this frame taller than its
      // content. Zero border/margin/padding keeps it a pure passthrough.
      style={{
        display: "block",
        width: "100%",
        height: "100%",
        border: "0",
        margin: 0,
        padding: 0,
      }}
      src="/bear-sandbox.html"
    />
  );
};

export default BearSandbox;
